/**
 * Timeout Error Class
 *
 * Why: Specialized handling for request timeouts
 * What: Error class for when requests take too long
 * BENEFITS:
 *      - Clear destinction from other network errors
 *      - Configurable timeout values
 *      - Retry with increased timeout option
 *
 * Use Cases:
 *      - Server taking too long to response
 *      - Network congestion
 *      - Long-running operations
 */

import { AppError, type AppErrorOptions } from "./base/AppError";
import { ErrorCategory, ErrorSeverity } from "../types";
import { ERROR_CODES } from "../constants";
import { ERROR_MESSAGES } from "../constants";

/**
 * Options specific to timeout errors
 */
export interface TimeoutErrorOptions extends Omit<
  AppErrorOptions,
  "statusCode"
> {
  /** The timeout duration that was exceeded (ms) */
  timeoutMs?: number;
  /** The URL that timed out */
  url?: string;
}

/**
 * Timeout Error
 *
 * Represents errors when a request exceeded the configuration timeout.
 * These are usually retryable as they mat be transient.
 */

export class TimeoutError extends AppError {
  /**
   * The timeout duration that was exceeded ms
   */
  public readonly timeoutMs?: number;

  /**
   * The URL that timeout
   */
  public readonly url?: string;

  constructor(
    message: string = ERROR_MESSAGES.NETWORK_TIMEOUT,
    options: TimeoutErrorOptions = {},
  ) {
    super(message, {
      statusCode: 408, // response timeout
      code: options.code ?? ERROR_CODES.NETWORK_TIMEOUT,
      category: options.category ?? ErrorCategory.TIMEOUT,
      severity: options.severity ?? ErrorSeverity.MEDIUM,
      isOperational: options.isOperational ?? true,
      isRetryable: options.isRetryable ?? true,
      context: options.context,
      cause: options.cause,
    });
    this.timeoutMs = options.timeoutMs;
    this.url = options.url;
  }

  /**
   * Create timeout error from axios timeout
   */
  static fromAxiosTimeout(
    config: { url?: string; timeout?: number },
    cause?: unknown,
  ): TimeoutError {
    return new TimeoutError(ERROR_MESSAGES.NETWORK_TIMEOUT, {
      timeoutMs: config.timeout,
      url: config.url,
      cause,
    });
  }

  /**
   * Get suggested new timeout value
   * Returns 1.5x the original timeout,capped at 60 seconds
   */
  public getSuggestedTimeout(): number {
    if (!this.timeoutMs) return 30000;
    return Math.min(this.timeoutMs * 1.5, 60000);
  }
}
